import { AlertTriangle, X } from "lucide-react";
import { useState } from "react";

import { readEnvName } from "@/lib/env";

interface NonProdAdvisoryBannerProps {
  /**
   * Stable key used to remember the user's dismissal for this banner
   * across renders within the same session (sessionStorage). Different
   * surfaces (e.g. feature-flags vs test-inbox) should use different
   * keys so dismissing one doesn't suppress the other.
   */
  dismissKey: string;
  /**
   * The fully-qualified prod URL of the equivalent surface. Rendered as
   * an underlined link in the banner ("manage from <link>").
   */
  prodUrl: string;
  /**
   * Short noun phrase naming the surface — used in the default message
   * text ("Feature flags are managed from …").
   */
  surfaceName: string;
  /**
   * Optional surface-specific extra body rendered after the standard
   * "managed from <prodUrl>" sentence. Use this to attach a domain note
   * (e.g. for feature-flags: "Toggles here only affect …") without
   * baking that copy into the reusable primitive. Receives the current
   * `VITE_ENV_NAME` so callers can templatize per-env wording.
   */
  renderBody?: (env: string) => React.ReactNode;
}

function loadDismissed(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function persistDismissed(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage unavailable — let the banner reappear next render
  }
}

/**
 * Soft notice that this surface is canonically managed from prod. Renders
 * nothing on prod ops console builds. Dismissible-per-session so it stays
 * out of the way once acknowledged.
 *
 * Built reusable so test-inbox and any future buy-not-build admin
 * surfaces can drop in with the same UX.
 */
export function NonProdAdvisoryBanner({
  dismissKey,
  prodUrl,
  surfaceName,
  renderBody,
}: NonProdAdvisoryBannerProps): React.ReactElement | null {
  const env = readEnvName();
  const [dismissed, setDismissed] = useState<boolean>(() =>
    loadDismissed(`non-prod-advisory:${dismissKey}`)
  );

  if (env === "prod") return null;
  if (dismissed) return null;

  function handleDismiss(): void {
    persistDismissed(`non-prod-advisory:${dismissKey}`);
    setDismissed(true);
  }

  return (
    <div
      data-testid={`non-prod-advisory-${dismissKey}`}
      className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 space-y-1">
        <p>
          <span className="font-medium">{surfaceName}</span> are managed from{" "}
          <a
            href={prodUrl}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
          >
            {prodUrl.replace(/^https?:\/\//, "")}
          </a>
          . Changes you make here only affect the <span className="font-mono uppercase">{env}</span>{" "}
          stack and won&apos;t reach end users.
        </p>
        {renderBody && <div>{renderBody(env)}</div>}
      </div>
      <button
        type="button"
        data-testid={`non-prod-advisory-${dismissKey}-dismiss`}
        onClick={handleDismiss}
        aria-label="Dismiss notice"
        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-amber-500/15 hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
